import React, { useCallback, useEffect, useState } from "react";
import NavBar from "../components/NavBar";
import API from "../api";
import { Plus, Trash2, Edit2, X, Trophy } from "lucide-react";

export default function TournamentsPage() {
  const [tournaments, setTournaments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, teamRes] = await Promise.all([API.get("/tournaments"), API.get("/teams")]);
      setTournaments(tRes.data); setTeams(teamRes.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this tournament?")) return;
    await API.delete(`/tournaments/${id}`); await load();
  };

  const teamById = (id) => teams.find(t => t.id === id);

  return (
    <div className="min-h-screen bg-slate-950">
      <NavBar subtitle="Tournaments" />
      <main className="pt-24 pb-16 max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-4xl font-extrabold uppercase tracking-tight">Tournaments</h1>
            <p className="text-slate-400 mt-1">Group teams for a league, series or tournament</p>
          </div>
          <button onClick={() => setEditing({ name: "", team_ids: [] })} data-testid="create-tournament-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 font-semibold">
            <Plus size={16} /> New Tournament
          </button>
        </div>

        {loading ? <div className="p-10 text-center text-slate-400">Loading...</div> :
          tournaments.length === 0 ? (
            <div className="glass rounded-2xl border border-slate-800 p-12 text-center">
              <Trophy className="mx-auto text-blue-500 mb-4" size={40}/>
              <div className="font-heading text-2xl font-bold uppercase">No tournaments yet</div>
              <p className="text-slate-400 mt-2">Create a tournament and attach the teams that will play in it.</p>
              <button onClick={() => setEditing({ name: "", team_ids: [] })} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 font-semibold">
                <Plus size={16}/> Create Tournament
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tournaments.map(t => (
                <div key={t.id} data-testid={`tournament-card-${t.id}`} className="glass rounded-xl border border-slate-800 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <Trophy className="text-amber-400 flex-shrink-0" size={22}/>
                      <div className="font-heading text-xl font-extrabold uppercase truncate">{t.name}</div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(t)} data-testid={`edit-tournament-${t.id}`}
                        className="p-2 rounded bg-blue-600/20 text-blue-300 hover:bg-blue-600/30 border border-blue-800"><Edit2 size={14}/></button>
                      <button onClick={() => remove(t.id)} data-testid={`delete-tournament-${t.id}`}
                        className="p-2 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-900"><Trash2 size={14}/></button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {t.team_ids.map(id => {
                      const team = teamById(id);
                      if (!team) return <span key={id} className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-500 italic">Removed team</span>;
                      return (
                        <span key={id} className="text-xs px-2 py-1 rounded font-semibold text-white" style={{backgroundColor: team.color}}>
                          {team.short_name || team.name}
                        </span>
                      );
                    })}
                    {t.team_ids.length === 0 && <span className="text-xs text-slate-500 italic">No teams attached</span>}
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </main>

      {editing && <TournamentModal tournament={editing} teams={teams} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function TournamentModal({ tournament, teams, onClose, onSaved }) {
  const isEdit = !!tournament.id;
  const [form, setForm] = useState({ name: tournament.name || "", team_ids: tournament.team_ids || [] });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const toggleTeam = (id) => setForm(f => ({
    ...f, team_ids: f.team_ids.includes(id) ? f.team_ids.filter(x => x !== id) : [...f.team_ids, id],
  }));

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (isEdit) await API.patch(`/tournaments/${tournament.id}`, form);
      else await API.post("/tournaments", form);
      onSaved(); onClose();
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="glass rounded-2xl border border-slate-700 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-extrabold uppercase">{isEdit ? "Edit Tournament" : "New Tournament"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded"><X size={18}/></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Tournament name (e.g. IPL 2026)" data-testid="tournament-form-name"
            className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700"/>

          <div>
            <label className="text-sm font-semibold text-slate-200 block mb-2">Teams ({form.team_ids.length} selected)</label>
            {teams.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4 border border-dashed border-slate-700 rounded-lg">
                Add teams first in the Teams Library.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                {teams.map(t => (
                  <label key={t.id} data-testid={`select-team-${t.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border ${form.team_ids.includes(t.id) ? "border-blue-500 bg-blue-500/10" : "border-slate-700 bg-slate-800/50 hover:bg-slate-800"}`}>
                    <input type="checkbox" checked={form.team_ids.includes(t.id)} onChange={() => toggleTeam(t.id)} className="accent-blue-500"/>
                    <div className="w-4 h-4 rounded" style={{backgroundColor: t.color}}/>
                    <span className="text-sm truncate">{t.short_name || t.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {err && <div className="text-sm p-2 rounded bg-red-500/10 border border-red-900 text-red-300">{err}</div>}
          <button type="submit" disabled={busy} data-testid="tournament-form-submit"
            className="w-full px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 font-semibold">
            {busy ? "Saving..." : isEdit ? "Save Changes" : "Create Tournament"}
          </button>
        </form>
      </div>
    </div>
  );
}
